import { useCallback, useEffect, useRef, useState } from 'react';
import MemoryPanel from '../components/MemoryPanel';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, FileText, MessageSquare, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAgentCard } from '../components/AgentDock';
import { PageLoader } from '../components/PageLoader';
import { generateMdx } from '../lib/markdown';
import { formatDateTime } from '../lib/text';
import type { DocumentReview, ReviewItem } from '../lib/types';

const KIND: Record<ReviewItem['kind'], string> = {
    new: 'Adds',
    changed: 'Changes',
    withdrawing: 'No longer stated',
};

const ADVICE: Record<NonNullable<ReviewItem['recommendation']>, { label: string; tone: string }> = {
    accept: { label: 'Accept', tone: 'bg-state-success/15 text-state-success' },
    reject: { label: 'Reject', tone: 'bg-state-error/10 text-state-error' },
    check: { label: 'Check', tone: 'bg-state-warning/25 text-graphite' },
};

// A claim's properties, one per line, as `Label: value`.
function Detail({ text }: { text: string }) {
    return (
        <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
            {text.split('\n').map((line, n) => {
                const at = line.indexOf(': ');
                return at === -1 ? (
                    <dd key={n} className="col-span-2 text-graphite">{line}</dd>
                ) : (
                    <div key={n} className="contents">
                        <dt className="text-coolslate">{line.slice(0, at)}</dt>
                        <dd className="text-graphite break-words min-w-0">{line.slice(at + 2)}</dd>
                    </div>
                );
            })}
        </dl>
    );
}

// What linked documents would add to the company record. The additions Navaris recommends
// are on the record already and listed as added; everything else waits for a person.
export default function ReviewPage() {
    const { authFetch } = useAuth();
    const { refresh } = useWorkspace();
    const { startChat } = useAgentCard();
    const [params, setParams] = useSearchParams();
    const [reviews, setReviews] = useState<DocumentReview[] | null>(null);
    const [updates, setUpdates] = useState<{ scope: string }[]>([]);
    const [leaving, setLeaving] = useState<Set<string>>(new Set());
    const settled = useRef(new Set<string>());
    const [open, setOpen] = useState<DocumentReview | null>(null);
    const [busy, setBusy] = useState(false);
    const selected = params.get('id');

    const loadList = useCallback(async () => {
        const res = await authFetch('/reviews').catch(() => null);
        setReviews(res?.ok ? await res.json() : []);
    }, [authFetch]);

    const loadOne = useCallback(async (id: string) => {
        const res = await authFetch(`/reviews/${id}`).catch(() => null);
        setOpen(res?.ok ? await res.json() : null);
    }, [authFetch]);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => {
        authFetch('/memory')
            .then(r => r.ok ? r.json() : [])
            .then(setUpdates)
            .catch(() => setUpdates([]));
    }, [authFetch]);
    useEffect(() => {
        if (selected) loadOne(selected);
        else if (reviews && reviews.length) setParams({ id: reviews[0].id }, { replace: true });
        else setOpen(null);
    }, [selected, reviews, loadOne, setParams]);

    const act = async (path: string, body?: object) => {
        if (!open) return;
        setBusy(true);
        try {
            await authFetch(`/reviews/${open.id}/${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body ?? {}),
            });
            await Promise.all([loadOne(open.id), loadList(), refresh()]);
        } finally {
            setBusy(false);
        }
    };

    // A settled update fades and folds away, then leaves the list.
    const letGo = useCallback((scope: string) => {
        if (settled.current.has(scope)) return;
        settled.current.add(scope);
        setLeaving(l => new Set(l).add(scope));
        setTimeout(() => setUpdates(u => u.filter(x => x.scope !== scope)), 1200);
    }, []);

    if (reviews === null) return <PageLoader label="Loading reviews..." />;

    const items = open?.items ?? [];
    const pending = items.filter(i => i.status === 'pending');
    const recommended = pending.filter(i => i.recommendation === 'accept');
    const groups = new Map<string, ReviewItem[]>();
    for (const i of items) groups.set(i.group_label, [...(groups.get(i.group_label) ?? []), i]);

    return (
        <div data-agent-view>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold">Review</h3>
                <p className="text-sm text-coolslate mt-1">
                    What your documents would add to the company record, and the updates Navaris suggests to the
                    memory of your company and its areas. Navaris adds the new records it recommends straight away
                    and lists them here; changes, removals and anything it flags wait for you to accept.
                </p>
            </div>

            {updates.length > 0 && (
                <div className="mb-8">
                    <h4 className="text-[15px] font-semibold text-graphite mb-4">Updates Navaris suggests to memory</h4>
                    {updates.map(t => (
                        <div key={t.scope}
                             className={`grid transition-[grid-template-rows,opacity] duration-[1200ms] ease-in-out ${
                                 leaving.has(t.scope) ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}>
                            <div className="overflow-hidden">
                                <div className="pb-4"><MemoryPanel scope={t.scope} titled onDecided={() => letGo(t.scope)} /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {reviews.length === 0 && updates.length === 0 && <p className="text-sm text-coolslate">Nothing is waiting for review.</p>}

            {reviews.length > 0 && (
                <div className="space-y-4">
                    <label className="flex flex-wrap items-center gap-2 text-sm text-coolslate">
                        <FileText className="w-4 h-4 shrink-0 text-brand-dark" />
                        Document
                        <select
                            aria-label="Document to review"
                            value={selected ?? ''}
                            onChange={e => setParams({ id: e.target.value })}
                            className="min-w-0 flex-1 sm:flex-none sm:max-w-xl px-3 py-2 text-sm border border-softgrey rounded-lg bg-white text-graphite hover:border-brand/30 focus:border-brand/40 focus:outline-none"
                        >
                            {reviews.map(r => (
                                <option key={r.id} value={r.id}>
                                    {r.file_name} · {r.counts?.pending ?? 0} waiting · {formatDateTime(r.created_at)}
                                </option>
                            ))}
                        </select>
                    </label>

                    {open && (
                        <section className="bg-white border border-softgrey rounded-xl p-5 min-w-0">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <Link to={`/files/${open.file_id}`} className="navaris-h3 hover:text-brand-dark">
                                        {open.file_name}
                                    </Link>
                                    <p className="text-xs text-coolslate mt-0.5">
                                        {pending.length} of {items.length} waiting for a decision
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => startChat(undefined, { ask: `Talk me through the review of ${open.file_name}` })}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-softgrey hover:border-brand/40"
                                    >
                                        <MessageSquare className="w-4 h-4" /> Discuss with Navaris
                                    </button>
                                    {recommended.length > 0 && (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => act('accept-recommended')}
                                            className="px-3 py-1.5 text-sm rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
                                        >
                                            Accept {recommended.length} recommended
                                        </button>
                                    )}
                                    {pending.length > 0 && (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => act('accept')}
                                            className="px-3 py-1.5 text-sm rounded-lg border border-brand/40 text-brand-dark hover:bg-brand/10 disabled:opacity-50"
                                        >
                                            Accept all
                                        </button>
                                    )}
                                </div>
                            </div>

                            {open.summary ? (
                                <div
                                    className="mt-4 p-3 rounded-lg bg-offwhite text-sm text-graphite prose-sm"
                                    dangerouslySetInnerHTML={{ __html: generateMdx(open.summary) }}
                                />
                            ) : (
                                <p className="mt-4 text-sm text-coolslate">Navaris is still looking at this document.</p>
                            )}

                            <div className="mt-5 space-y-5">
                                {[...groups.entries()].map(([group, rows]) => (
                                    <div key={group}>
                                        <h4 className="text-sm font-semibold text-graphite mb-2">{group}</h4>
                                        <ul className="divide-y divide-softgrey/60 border border-softgrey rounded-lg">
                                            {rows.map(i => (
                                                <li key={i.id} className={`flex items-start gap-3 px-3 py-2.5 ${i.status === 'pending' ? '' : 'opacity-60'}`}>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-[11px] uppercase tracking-wide text-coolslate">{KIND[i.kind]}</span>
                                                            <span className="text-sm text-graphite">{i.title}</span>
                                                            {i.recommendation && i.status === 'pending' && (
                                                                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${ADVICE[i.recommendation].tone}`}>
                                                                    {ADVICE[i.recommendation].label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {i.detail && <Detail text={i.detail} />}
                                                        {i.reason && i.status === 'pending' && <p className="text-xs text-graphite/80 mt-1">{i.reason}</p>}
                                                    </div>
                                                    {i.status === 'pending' ? (
                                                        <div className="flex gap-1 shrink-0">
                                                            <button
                                                                type="button"
                                                                disabled={busy}
                                                                onClick={() => act('accept', { item_ids: [i.id] })}
                                                                aria-label={`Accept ${i.title}`}
                                                                title="Accept"
                                                                className="w-7 h-7 grid place-items-center rounded-md text-state-success hover:bg-state-success/10 disabled:opacity-50"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={busy}
                                                                onClick={() => act('reject', { item_ids: [i.id] })}
                                                                aria-label={`Reject ${i.title}`}
                                                                title="Reject"
                                                                className="w-7 h-7 grid place-items-center rounded-md text-state-error hover:bg-state-error/10 disabled:opacity-50"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-coolslate shrink-0 capitalize">
                                                            {i.status === 'accepted' && !i.decided_by ? 'Added by Navaris' : i.status}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
}
